<template>
  <div class="p-3 w-full flex flex-col gap-2 m-auto h-[700px]">
    <Toolbar>
      <template #start>
        <div class="flex gap-2">
          <Button :label="editButtonLabel" :icon="editButtonIcon" severity="surface" @click="toggleEditing"></Button>
          <Button v-if="editing" label="Save" icon="pi pi-save" @click="saveExportLayout"></Button>
        </div>
      </template>
    </Toolbar>
    <div class="w-full flex-auto">
      <ag-grid-vue
        re
        theme="legacy"
        :rowData="ledger.ledgerExportList"
        :columnDefs="columns"
        :defaultColDef="defaultColDefs"
        :autoSizeStrategy="{ type: 'fitCellContents' }"
        :singleClickEdit="true"
        :stopEditingWhenCellsLoseFocus="true"
        :suppressDragLeaveHidesColumns="true"
        rowSelection="single"
        class="py-3 h-full"
        :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
        @grid-size-changed="resize"
        @cell-editing-stopped="cellEditComplete"
        @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { LedgerExportListSchema, type LedgerDashboard, type LedgerExport } from "@/types/Ledger";
import { getCellEditableClass } from "@/services/Utility";
import { usePropsStore } from "@/stores/props";
import type { CellClassParams, EditableCallbackParams, GridApi, GridReadyEvent } from "ag-grid-community";

const ledger = defineModel<LedgerDashboard>("ledger", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });

const gridApi = ref<GridApi>();
const ledgerHistory = useManualRefHistory(ledger, { clone: true });

const propsStore = usePropsStore();
const darkVal = ref(propsStore.darkMode);

const editButtonLabel = computed<"Cancel" | "Edit">(() => (editing.value ? "Cancel" : "Edit"));
const editButtonIcon = computed<string>(() => (editing.value ? "pi pi-times" : "pi pi-pencil"));

watch(
  () => propsStore.darkMode,
  (n: boolean) => (darkVal.value = n)
);

watch(editing, (n: boolean) => gridApi.value?.refreshCells({ force: true }));

onBeforeUnmount((): void => {
  if (editing.value) {
    toggleEditing();
  }
});

const toggleEditing = (): void => {
  if (editing.value) {
    editing.value = false;
    ledgerHistory.undo();
    editingTitle.value = "";
  } else {
    editing.value = true;
    ledgerHistory.commit();
    editingTitle.value = "Editing Ledger Export Configuration";
  }
};

const saveExportLayout = (): void => {
  if (!editing.value) return;

  axios
    .post(`/ledger/${ledger.value.checkingAccount.checkingAccountId}/export/update`, {
      exportRows: ledger.value.ledgerExportList,
    })
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          ledgerHistory.commit();
          editing.value = false;
          editingTitle.value = "";
          ledger.value.ledgerExportList = LedgerExportListSchema.parse(data);
          success("Success", "Ledger export configuration updated");
        },
        (errors: ApiError[]) => {}
      )
    )
    .catch((e) => errorApi(e));
};

const resize = (): void => {
  gridApi.value?.autoSizeColumns([]);
};

const cellClass = (params: any): string => {
  if (params.colDef.editable && params.colDef.editable(params)) {
    return getCellEditableClass();
  }
  return "";
};

const defaultColDefs = {
  floatingFilter: false,
  sortable: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellClass: (params: CellClassParams) => cellClass(params),
};

const cellEditComplete = async (rowNode: any): Promise<void> => {
  const colId = rowNode.column.colId;
  if (rowNode.newValue !== rowNode.oldValue && colId === "transactionMethod") {
    const exportItem: LedgerExport = rowNode.data;

    if (rowNode.newValue !== "DDA") {
      exportItem.dDAAccount = "";
    }

    if (rowNode.newValue !== "DDA" || rowNode.newValue !== "GL") {
      exportItem.debitSource = 0;
      exportItem.debitSourceCode = "";
      exportItem.creditTranCode = "";
      exportItem.accountNumber = "";
      exportItem.blockSeq = "";
      exportItem.costCenter = "";
      exportItem.constructionLoanAccount = "";
      exportItem.equityDebitTranCode = "";
      exportItem.equitySourceCode = "";
    }

    if (rowNode.newValue === "") {
      exportItem.debitTranCode = "";
      exportItem.description = "";
    }

    gridApi.value?.refreshCells({ force: true });
  }
};

const columns = ref([
  {
    field: "transactionType",
    headerName: "Transaction",
    width: 150,
    pinned: true,
  },
  {
    field: "transactionMethod",
    headerName: "Transaction Type",
    headerClass: "grid-align-center",
    width: 100,
    pinned: true,
    editable: () => editing.value,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["", "GL", "DDA", "INT"],
    },
  },
  {
    field: "constructionLoanAccount",
    headerName: "Debit Account #",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "debitTranCode",
    headerName: "Debit Tran Code",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) => editing.value && params.data.transactionMethod,
  },
  {
    field: "debitSource",
    headerName: "Debit Source",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) => editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod == "DDA"),
  },
  {
    field: "debitSourceCode",
    headerName: "Debit Source Code",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "equityDebitTranCode",
    headerName: "Equity Debit Tran Code",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "equitySourceCode",
    headerName: "Equity Debit Source Code",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "creditTranCode",
    headerName: "Deposit Tran Code",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "accountNumber",
    headerName: "Deposit Account #",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "costCenter",
    headerName: "Cost Center",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "dDAAccount",
    headerName: "DDA Account",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) => editing.value && params.data.transactionMethod === "DDA",
  },
  {
    field: "blockSeq",
    headerName: "Block/Seq",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) =>
      editing.value && (params.data.transactionMethod === "GL" || params.data.transactionMethod === "DDA"),
  },
  {
    field: "description",
    headerName: "Description",
    headerClass: "grid-align-center",
    width: 75,
    editable: (params: EditableCallbackParams) => editing.value && params.data.transactionMethod,
  },
]);
</script>
